// @flow

import React, {Component} from 'react';
import CheckBox from '@pb/kit-component-checkbox';
import {shouldUpdate} from '@pb/kit-util-should-update';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {TreeContainer} from './tree-container.component';
import {parseCssClasses, getExpandedFlag} from './utils';
import styles from './tree.component.module.styl';

import type {Element, ElementProps} from 'react';

import type {TreeItem, ExpandedTree, ExpandedTreeItem, CssClassesType, NodeProps} from './typedef';
import type {PrefixType} from './constants';


const classNames = withCssModule(styles);
const classes = hashedClasses(styles);


export type Props = {|
    ...$Exact<ElementProps<'div'>>,
    expandedIdsTree: ExpandedTreeItem,
    updateExpandedIds: (expandedIdsTree: ExpandedTree) => void,
    updateExpandedIdsTree: (expandedIdsTree: ExpandedTreeItem, id: string) => void,
    data: TreeItem,
    getNode: (props: NodeProps) => Element<any>,
    disabled: boolean,
    isFlat: boolean,
    prefixType: PrefixType,
    prefix: string,
|};

type State = {|
    expanded: boolean,
    cssClasses: CssClassesType,
|};

export class TreeNode extends Component<Props, State> {
    state = {
        expanded: getExpandedFlag({
            data: this.props.data,
            expandedIdsTree: this.props.expandedIdsTree,
            isFlat: this.props.isFlat
        }),
        cssClasses: parseCssClasses(this.props.data)
    };

    componentDidUpdate(prevProps: Props) {
        const {data, expandedIdsTree, isFlat} = this.props;
        if (shouldUpdate(prevProps, this.props, ['expandedIdsTree'])) {
            this.setState({
                expanded: getExpandedFlag({data, expandedIdsTree, isFlat})
            });
        }
    }

    onToggleExpanded = () => {
        const expanded = !this.state.expanded;
        const {data, expandedIdsTree} = this.props;
        this.props.updateExpandedIdsTree({...expandedIdsTree, expanded}, data.id);
        this.setState({expanded});
    };

    onSubTreeExpandUpdated = (updatedExpandedIdsTree: ExpandedTree) => {
        const {data, expandedIdsTree} = this.props;
        const {expanded} = this.state;
        this.props.updateExpandedIdsTree({...expandedIdsTree, expanded, children: updatedExpandedIdsTree}, data.id);
    };

    renderExpander() {
        const {
            data: {transient, children = [], alwaysExpanded, alwaysExpandedSubcategory, matchedSearch = true},
        } = this.props;
        const {expanded, cssClasses} = this.state;

        if (transient || children.length === 0 || alwaysExpanded
            || alwaysExpandedSubcategory || !matchedSearch || cssClasses.hidden) {
            return null;
        }

        return (
            <div className={classes.tree__nested}>
                <CheckBox
                    name="tree-expander"
                    aria-label={`${expanded ? 'Collapse' : 'Expand'} ${this.props.data.caption ?? ''}`.trim()}
                    custom={true}
                    checked={expanded}
                    onCheckBoxChange={this.onToggleExpanded}
                />
            </div>
        );
    }

    renderNode() {
        const {cssClasses} = this.state;
        const {data, getNode, disabled, prefix} = this.props;

        return !cssClasses.hidden && getNode({
            data,
            disabled,
            cssClasses,
            prefix
        });
    }

    render() {
        const {expanded, cssClasses} = this.state;
        const {
            data,
            getNode,
            expandedIdsTree,
            disabled,
            isFlat,
            prefix,
            prefixType,
            'aria-level': ariaLevel = 1,
        } = this.props;
        const {
            matchedSearch = true,
            alwaysExpanded,
            alwaysExpandedSubcategory,
            transient: transientFlag
        } = data;
        const hasChildren = data.children && data.children.length > 0;

        const treeItemClasses = classNames('tree__item', {
            'tree__item_always-expanded': isFlat && !transientFlag || alwaysExpanded && !alwaysExpandedSubcategory,
            'tree__item_bold-shifted': cssClasses.bold,
            'tree__item_no-top-margin': !matchedSearch || cssClasses.hidden
        });

        const treeBoxClasses = classNames('tree__box-v2', {
            'tree__box-v2_transient': transientFlag
        });

        return (
            <div
                className={treeItemClasses}
                role="treeitem"
                aria-level={ariaLevel}
                {...(hasChildren ? {'aria-expanded': expanded} : null)}>
                {this.renderExpander()}
                <div className={treeBoxClasses}>
                    {this.renderNode()}
                    {hasChildren && expanded ? (
                        <TreeContainer
                            aria-level={ariaLevel + 1}
                            {...{
                                expandedIdsTree: expandedIdsTree.children || {},
                                updateExpandedIds: this.onSubTreeExpandUpdated,
                                data: data.children,
                                getNode,
                                disabled,
                                prefix,
                                prefixType
                            }}
                        />
                    ) : null}
                </div>
            </div>
        );
    }
}
